/**
 * proofs/utils.ts — nhận diện đơn ảnh thẻ + đọc trạng thái bằng chứng.
 *
 * Đây chỉ là lớp bật/tắt giao diện. Server có bản chặn riêng
 * (order.IsIDPhoto + 2 gate) và luôn là bên nói lời cuối.
 */
import { getOrderCategory } from "@/lib/orderUtils";
import type { components } from "@/lib/api/generated/schema";

/**
 * Bản ghi như người đang xem thấy — `removable` do máy chủ tính theo đúng luật
 * gỡ (người chụp + còn hạn nộp, hoặc quản trị/giám đốc). Màn KHÔNG tự suy lại.
 */
export type OrderProof = components["schemas"]["OrderProofView"];
export type ProofKind = OrderProof["kind"];

/** Trần ảnh mỗi lần gửi — khớp proof.MaxImages phía server. */
export const MAX_PROOF_IMAGES = 10;

/** Đơn ảnh thẻ — loại duy nhất bắt buộc có ảnh bằng chứng giao. */
export function isIdPhotoOrder(o: {
  category?: string | null;
  product?: string | null;
  productId?: string | null;
}): boolean {
  return (
    getOrderCategory({
      category: o.category ?? null,
      product: o.product ?? null,
      productId: o.productId ?? null,
    }) === "idphoto"
  );
}

/** Mặt của một bản ghi — bản ghi trước chấm QC bản 3 không có số mặt = Mặt 1. */
export function faceOf(p: Pick<OrderProof, "face">): number {
  return p.face ?? 1;
}

/** Các mặt người này đã nộp ảnh trước/sau, tăng dần. */
export function myMakeupFaces(proofs: OrderProof[], staffId: string): number[] {
  if (!staffId) return [];
  const faces = new Set(
    proofs
      .filter((p) => p.takenBy === staffId && (p.kind === "makeup_before" || p.kind === "makeup_after"))
      .map(faceOf),
  );
  return [...faces].sort((a, b) => a - b);
}

/**
 * Đã đủ cặp trước/sau CỦA CHÍNH NGƯỜI NÀY chưa (gate nút Hoàn tất ca): có ít
 * nhất một mặt, và MỌI mặt đã nộp đều có cả trước lẫn sau (chấm QC bản 3, 2a)
 * — cùng luật `HasMakeupPair` của máy chủ.
 */
export function hasMakeupPair(proofs: OrderProof[], staffId: string): boolean {
  const faces = myMakeupFaces(proofs, staffId);
  if (faces.length === 0) return false;
  const mine = proofs.filter((p) => p.takenBy === staffId);
  return faces.every(
    (f) =>
      mine.some((p) => faceOf(p) === f && p.kind === "makeup_before") &&
      mine.some((p) => faceOf(p) === f && p.kind === "makeup_after"),
  );
}

export function proofsOfKind(proofs: OrderProof[], kind: ProofKind): OrderProof[] {
  return proofs.filter((p) => p.kind === kind);
}

/** Một tấm ảnh kèm bản ghi chứa nó — đơn vị để vẽ ô và để gỡ đúng một tấm. */
export type ProofPhoto = { src: string; proof: OrderProof };

/**
 * Trải mọi tấm của các bản ghi thành một hàng, cũ trước mới sau. Mỗi lần gửi
 * là một bản ghi, và một bản ghi giữ tới 10 tấm — trước đây màn chỉ lấy tấm
 * đầu của bản ghi đầu nên "chụp thêm" bao nhiêu cũng như không thấy.
 */
export function photosOf(proofs: OrderProof[]): ProofPhoto[] {
  return proofs.flatMap((proof) => proof.images.map((src) => ({ src, proof })));
}

/** Mọi tấm của riêng một người theo một loại — hàng ảnh trên thẻ ca. `face` = chỉ mặt đó. */
export function myPhotosOfKind(
  proofs: OrderProof[],
  staffId: string,
  kind: ProofKind,
  face?: number,
): ProofPhoto[] {
  if (!staffId) return [];
  return photosOf(
    proofs.filter(
      (p) => p.takenBy === staffId && p.kind === kind && (face === undefined || faceOf(p) === face),
    ),
  );
}

export const KIND_LABEL: Record<ProofKind, string> = {
  // Cùng tên với khối trên chi tiết đơn (CEO chốt 3a, 07/10/2026); "Ảnh giao
  // khách" trùng tên thư mục ảnh hoàn thiện của album nên dễ đọc nhầm.
  delivery: "Ảnh chụp cùng khách",
  makeup_before: "Trước trang điểm",
  makeup_after: "Sau trang điểm",
};
